import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postInventoryAdjust } from '@/lib/api/inventory.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

const ADD_REASONS = [
  { value: 'found', label: 'Found' },
  { value: 'vendor_credit', label: 'Vendor Credit' },
  { value: 'opening_balance', label: 'Opening Balance' },
  { value: 'other', label: 'Other' },
] as const;

const REMOVE_REASONS = [
  { value: 'damaged', label: 'Damaged' },
  { value: 'theft', label: 'Theft' },
  { value: 'data_entry_error', label: 'Data Entry Error' },
  { value: 'expired', label: 'Expired' },
  { value: 'other', label: 'Other' },
] as const;

export function StockAdjustModal({
  open,
  onClose,
  itemId,
  itemName,
  currentQty,
  suppliers,
  onSuccess,
  onPrintedLabelsOffer,
}: {
  open: boolean;
  onClose: () => void;
  itemId: string;
  itemName: string;
  currentQty: number;
  suppliers: Array<{ id: string; supplierName?: string; supplierId?: string }>;
  onSuccess: () => void;
  /** Called after a successful stock increase so staff can print labels */
  onPrintedLabelsOffer?: (qtyAdded: number) => void | Promise<void>;
}) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'add' | 'remove'>('add');
  const [qty, setQty] = useState('1');
  const [reasonCode, setReasonCode] = useState<string>('found');
  const [notes, setNotes] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [supplierVariantId, setSupplierVariantId] = useState('');

  const reasons = mode === 'add' ? ADD_REASONS : REMOVE_REASONS;

  useEffect(() => {
    if (open) {
      setMode('add');
      setQty('1');
      setReasonCode('found');
      setNotes('');
      setUnitCost('');
      setSupplierVariantId('');
    }
  }, [open, itemId]);

  const deltaNum = Number(qty) || 0;
  const signedDelta = mode === 'add' ? deltaNum : -deltaNum;
  const afterQty = currentQty + signedDelta;

  const mutation = useMutation({
    mutationFn: () =>
      postInventoryAdjust(itemId, {
        delta: signedDelta,
        reasonCode: reasonCode as never,
        reasonNotes: notes || undefined,
        adjustmentType: 'manual',
        unitCost:
          mode === 'add' && unitCost ? Number(unitCost) : undefined,
        supplierVariantId: mode === 'add' && supplierVariantId ? supplierVariantId : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-item', itemId] });
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
      toastSuccess(`Stock adjusted: ${signedDelta >= 0 ? '+' : ''}${signedDelta} units (now ${afterQty})`);
      onSuccess();
      onClose();
      if (mode === 'add' && signedDelta > 0 && onPrintedLabelsOffer) {
        void onPrintedLabelsOffer(signedDelta);
      }
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  if (!open) return null;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (deltaNum <= 0) {
      toastError('Enter a valid quantity');
      return;
    }
    if (afterQty < 0) {
      toastError('Resulting quantity cannot be negative');
      return;
    }
    mutation.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="text-lg font-bold text-slate-950">Adjust stock</h3>
        <p className="mt-1 text-sm text-slate-600">{itemName}</p>
        <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
          <div className="flex gap-2">
            <button
              type="button"
              className={[
                'flex-1 rounded-xl border px-3 py-2 text-sm font-semibold',
                mode === 'add' ? 'border-green-600 bg-green-50 text-green-900' : 'border-slate-200 text-slate-700',
              ].join(' ')}
              onClick={() => {
                setMode('add');
                setReasonCode('found');
              }}
            >
              + Add stock
            </button>
            <button
              type="button"
              className={[
                'flex-1 rounded-xl border px-3 py-2 text-sm font-semibold',
                mode === 'remove' ? 'border-red-600 bg-red-50 text-red-900' : 'border-slate-200 text-slate-700',
              ].join(' ')}
              onClick={() => {
                setMode('remove');
                setReasonCode('damaged');
              }}
            >
              − Remove stock
            </button>
          </div>
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Quantity</span>
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              type="number"
              min="1"
              step="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              required
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Reason</span>
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
            >
              {reasons.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Notes (optional)</span>
            <textarea
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
          {mode === 'add' ? (
            <>
              <label className="block text-sm">
                <span className="font-medium text-slate-700">Unit cost (FIFO layer)</span>
                <input
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                  type="number"
                  min="0"
                  step="0.01"
                  value={unitCost}
                  onChange={(e) => setUnitCost(e.target.value)}
                  placeholder="0.00"
                />
              </label>
              {suppliers.length ? (
                <label className="block text-sm">
                  <span className="font-medium text-slate-700">Supplier (optional)</span>
                  <select
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                    value={supplierVariantId}
                    onChange={(e) => setSupplierVariantId(e.target.value)}
                  >
                    <option value="">—</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.supplierName ?? s.id}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </>
          ) : null}
          <div className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-700">
            Current: <span className="font-semibold">{currentQty}</span> units → After:{' '}
            <span className="font-semibold">{afterQty}</span> units
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-[#164477] disabled:opacity-60"
            >
              {mutation.isPending ? 'Saving…' : 'Save adjustment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
